import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { conversationsAPI, usersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Plus,
  Search,
  Users,
  FileText,
  User,
  Mail,
  AtSign,
  Loader2,
  Check,
  CheckCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function MessagingPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New message state
  const [newMessage, setNewMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [sendAsEmail, setSendAsEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  
  // New conversation dialog
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newConvParticipants, setNewConvParticipants] = useState([]);
  const [newConvTitle, setNewConvTitle] = useState('');
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Auto-select conversation from URL param
    const convId = searchParams.get('conv');
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === convId);
      if (conv) {
        selectConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchData = async () => {
    try {
      const [convsRes, usersRes] = await Promise.all([
        conversationsAPI.getConversations(),
        usersAPI.getAll()
      ]);
      setConversations(convsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setSearchParams({ conv: conv.id });
    setLoadingMessages(true);
    try {
      const res = await conversationsAPI.getMessages(conv.id);
      setMessages(res.data);
      // Update unread count
      setConversations(prev => prev.map(c => 
        c.id === conv.id ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await conversationsAPI.sendMessage(selectedConversation.id, {
        content: newMessage,
        mentions: selectedMentions.map(u => u.id),
        send_as_email: sendAsEmail,
        email_to: sendAsEmail ? emailTo : null,
        email_subject: sendAsEmail ? emailSubject : null,
      });
      
      setMessages([...messages, res.data]);
      setNewMessage('');
      setSelectedMentions([]);
      setSendAsEmail(false);
      setEmailTo('');
      setEmailSubject('');
      
      // Update conversation last message
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, last_message: newMessage.substring(0, 100), last_message_at: new Date().toISOString() }
          : c
      ));
      
      if (sendAsEmail) {
        toast.success('Message envoyé et email envoyé');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleCreateConversation = async () => {
    if (newConvParticipants.length === 0) {
      toast.error('Sélectionnez au moins un participant');
      return;
    }
    
    try {
      const res = await conversationsAPI.createConversation({
        title: newConvTitle || null,
        participant_ids: newConvParticipants,
        type: newConvParticipants.length > 1 ? 'group' : 'direct',
      });
      
      setConversations([res.data, ...conversations]);
      setSelectedConversation(res.data);
      setShowNewConversation(false);
      setNewConvParticipants([]);
      setNewConvTitle('');
      toast.success('Conversation créée');
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleMentionClick = (mentionedUser) => {
    if (!selectedMentions.find(u => u.id === mentionedUser.id)) {
      setSelectedMentions([...selectedMentions, mentionedUser]);
    }
    setShowMentions(false);
    setMentionSearch('');
    messageInputRef.current?.focus();
  };

  const removeMention = (userId) => {
    setSelectedMentions(prev => prev.filter(u => u.id !== userId));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    if (e.key === '@') {
      setShowMentions(true);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.title?.toLowerCase().includes(search) ||
      conv.participant_names?.some(name => name.toLowerCase().includes(search)) ||
      conv.related_entity_name?.toLowerCase().includes(search)
    );
  });

  const getConversationIcon = (type) => {
    switch (type) {
      case 'devis': return <FileText className="w-4 h-4" />;
      case 'client': return <User className="w-4 h-4" />;
      case 'group': return <Users className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4">
      {/* Conversations List */}
      <Card className="w-80 flex-shrink-0 flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Messagerie</CardTitle>
            <Button size="sm" onClick={() => setShowNewConversation(true)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune conversation</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={cn(
                  'w-full text-left p-3 rounded-lg transition-colors',
                  selectedConversation?.id === conv.id
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'hover:bg-slate-50'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                    conv.type === 'devis' ? 'bg-blue-100 text-blue-600' :
                    conv.type === 'client' ? 'bg-green-100 text-green-600' :
                    'bg-slate-100 text-slate-600'
                  )}>
                    {getConversationIcon(conv.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm text-slate-800 truncate">
                        {conv.title || conv.participant_names?.filter(n => n !== user?.name).join(', ') || 'Conversation'}
                      </p>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-yellow-500 text-white text-[10px] h-5 min-w-5">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    {conv.related_entity_name && (
                      <p className="text-xs text-yellow-600 truncate">{conv.related_entity_name}</p>
                    )}
                    {conv.last_message && (
                      <p className="text-xs text-slate-500 truncate mt-1">{conv.last_message}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {formatTime(conv.last_message_at || conv.updated_at)}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col">
        {!selectedConversation ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Sélectionnez une conversation</p>
              <p className="text-sm mt-1">ou créez-en une nouvelle</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  selectedConversation.type === 'devis' ? 'bg-blue-100 text-blue-600' :
                  selectedConversation.type === 'client' ? 'bg-green-100 text-green-600' :
                  'bg-slate-100 text-slate-600'
                )}>
                  {getConversationIcon(selectedConversation.type)}
                </div>
                <div>
                  <CardTitle className="text-base">
                    {selectedConversation.title || selectedConversation.participant_names?.filter(n => n !== user?.name).join(', ')}
                  </CardTitle>
                  {selectedConversation.related_entity_name && (
                    <p className="text-xs text-yellow-600">{selectedConversation.related_entity_name}</p>
                  )}
                  <p className="text-xs text-slate-500">
                    {selectedConversation.participant_names?.length} participants
                  </p>
                </div>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>Aucun message</p>
                  <p className="text-sm">Commencez la conversation !</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwnMessage = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={cn('flex gap-3', isOwnMessage && 'flex-row-reverse')}
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarFallback className={cn(
                          'text-xs',
                          isOwnMessage ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'
                        )}>
                          {getInitials(msg.sender_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={cn('max-w-[70%]', isOwnMessage && 'items-end')}>
                        <div className={cn('flex items-center gap-2 mb-1', isOwnMessage && 'flex-row-reverse')}>
                          <span className="text-xs font-medium text-slate-700">{msg.sender_name}</span>
                          <span className="text-[10px] text-slate-400">{formatTime(msg.created_at)}</span>
                          {msg.is_email && (
                            <Badge variant="outline" className="text-[10px] h-4">
                              <Mail className="w-3 h-3 mr-1" />
                              Email
                            </Badge>
                          )}
                        </div>
                        <div className={cn(
                          'rounded-xl px-4 py-2',
                          isOwnMessage 
                            ? 'bg-yellow-500 text-white rounded-tr-sm' 
                            : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                        )}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          {msg.mentions?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {msg.mentions.map(m => (
                                <span key={m.user_id} className={cn(
                                  'text-xs px-1.5 py-0.5 rounded',
                                  isOwnMessage ? 'bg-yellow-600' : 'bg-slate-200'
                                )}>
                                  @{m.user_name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {isOwnMessage && (
                          <div className="flex justify-end mt-1">
                            {msg.read_by?.length > 1 ? (
                              <CheckCheck className="w-3 h-3 text-yellow-500" />
                            ) : (
                              <Check className="w-3 h-3 text-slate-400" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Message Input */}
            <div className="p-4 border-t bg-slate-50">
              {/* Mentions display */}
              {selectedMentions.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedMentions.map(u => (
                    <Badge 
                      key={u.id} 
                      variant="secondary" 
                      className="cursor-pointer hover:bg-red-100"
                      onClick={() => removeMention(u.id)}
                    >
                      @{u.name} ×
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Email options */}
              {sendAsEmail && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input
                    placeholder="Email du destinataire"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder="Sujet (optionnel)"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              )}
              
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Textarea
                    ref={messageInputRef}
                    placeholder="Écrivez votre message... (@ pour mentionner)"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="min-h-[60px] resize-none pr-20"
                  />
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn('h-7 w-7 p-0', sendAsEmail && 'text-yellow-600 bg-yellow-50')}
                      onClick={() => setSendAsEmail(!sendAsEmail)}
                      title="Envoyer aussi par email"
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setShowMentions(!showMentions)}
                      title="Mentionner quelqu'un"
                    >
                      <AtSign className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Mentions dropdown */}
                  {showMentions && (
                    <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <Input
                        placeholder="Rechercher..."
                        value={mentionSearch}
                        onChange={(e) => setMentionSearch(e.target.value)}
                        className="border-0 border-b rounded-none"
                        autoFocus
                      />
                      {users
                        .filter(u => u.id !== user?.id)
                        .filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch.toLowerCase()))
                        .map(u => (
                          <button
                            key={u.id}
                            onClick={() => handleMentionClick(u)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                          >
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="text-[10px]">{getInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{u.name}</span>
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="h-[60px] w-[60px] bg-yellow-500 hover:bg-yellow-600"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titre (optionnel)</Label>
              <Input
                placeholder="Nom de la conversation"
                value={newConvTitle}
                onChange={(e) => setNewConvTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Participants</Label>
              <div className="border rounded-lg max-h-64 overflow-y-auto mt-2">
                {users.filter(u => u.id !== user?.id).map(u => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={newConvParticipants.includes(u.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewConvParticipants([...newConvParticipants, u.id]);
                        } else {
                          setNewConvParticipants(prev => prev.filter(id => id !== u.id));
                        }
                      }}
                    />
                    <Avatar className="w-8 h-8">
                      <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewConversation(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateConversation}>
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
